import React, { useState, useEffect, useCallback } from 'react'
import { useProgram } from '@/hooks/useProgram'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
	CardDescription,
} from '@/components/ui/card'
import { useToast } from '@/components/ui/use-toast'
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import {
	Trash2,
	Loader2,
	Save,
	SettingsIcon,
	Eye,
	Globe as GlobeIcon,
	Layers,
	Copy as CopyIcon,
	ExternalLink,
	AlertOctagon,
	Settings2,
	Wrench,
} from 'lucide-react'
import { getAuthHeaders, widgetApi } from '@/services/api'
import { getApiBaseUrl } from '@/lib/api'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormMessage,
	FormDescription,
} from '@/components/ui/form'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select'
import { motion } from 'framer-motion'
import { useTeamContext } from '@/context/TeamContext'
import { useProgramContext } from '@/context/ProgramContext'
import { canManageProjectSettings } from '@/lib/programPermissions'
import { useTranslation } from '@/hooks/useTranslation'

function normalizeWebsiteUrl(raw: string): string {
	const t = raw.trim()
	if (!t) return ''
	if (!t.startsWith('http://') && !t.startsWith('https://')) {
		return `https://${t}`
	}
	return t
}

// Define Widget type based on expected API response for widget list
interface WidgetListItem {
	id: string
	name: string
	// Add other relevant fields if needed for display or filtering
}

// Zod schema for the form
const getProgramSettingsSchema = (t: (key: string) => string) => z
	.object({
		programName: z.string().min(1, t('programSettings.programNameEmpty')),
		enablePublicReviews: z.boolean().optional(),
		publicPageUrlSlug: z
			.string()
			.regex(/^[a-z0-9-]+$/, {
				message: t('programSettings.slugRegexError'),
			})
			.optional()
			.or(z.literal('')),
		logoUrl: z
			.string()
			.url({ message: 'Invalid URL format.' })
			.or(z.literal(''))
			.optional(),
		projectSiteUrl: z
			.string()
			.url({ message: 'Invalid URL format.' })
			.or(z.literal(''))
			.optional(),
		mainWebsiteUrl: z
			.string()
			.url({ message: 'Invalid URL format.' })
			.or(z.literal(''))
			.optional(),
		publicPageDescription: z
			.string()
			.max(500, t('programSettings.descriptionMaxLength'))
			.optional(),
		showAggregateStats: z.boolean().optional(),
		allowNewReviewSubmissions: z.boolean().optional(),
		reviewSubmissionWidgetId: z.string().optional(), // Can be widget UUID or "none"
		defaultReviewSortOrder: z.string().optional(),
		pageTitleSuffix: z.string().optional(),
		metaDescription: z
			.string()
			.max(160, t('programSettings.metaDescriptionMaxLength'))
			.optional(),
	})
	.superRefine((data, ctx) => {
		if (
			data.allowNewReviewSubmissions &&
			(!data.reviewSubmissionWidgetId ||
				data.reviewSubmissionWidgetId === 'none')
		) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: t('programSettings.widgetMustBeSelected'),
				path: ['reviewSubmissionWidgetId'],
			})
		}
	})

type ProgramSettingsFormValues = z.infer<ReturnType<typeof getProgramSettingsSchema>>

const ProgramSettings = () => {
	const { t } = useTranslation()
	const { teamId, role: teamRole } = useTeamContext()
	const { currentProgram, fetchPrograms, programs } = useProgram()
	const { currentProgramAccessLevel, isInvitedProgram } = useProgramContext()
	const { toast } = useToast()

	const [isDeleting, setIsDeleting] = useState(false)
	const [isSaving, setIsSaving] = useState(false)
	const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
	const [baseUrl, setBaseUrl] = useState('')
	const [siteMetaLoading, setSiteMetaLoading] = useState(false)
	const [siteFaviconPreview, setSiteFaviconPreview] = useState<string | null>(null)
	const [siteDescriptionPreview, setSiteDescriptionPreview] = useState<string | null>(null)
	const [faviconImgFailed, setFaviconImgFailed] = useState(false)

	const programSettingsSchema = React.useMemo(() => getProgramSettingsSchema(t), [t])
	const form = useForm<ProgramSettingsFormValues>({
		resolver: zodResolver(programSettingsSchema),
		defaultValues: {
			programName: '',
			projectSiteUrl: '',
			enablePublicReviews: false,
			publicPageUrlSlug: '',
			logoUrl: '',
			mainWebsiteUrl: '',
			publicPageDescription: '',
			showAggregateStats: true,
			allowNewReviewSubmissions: true,
			reviewSubmissionWidgetId: 'none',
			defaultReviewSortOrder: 'newest',
			pageTitleSuffix: '',
			metaDescription: '',
		},
	})

	// Watch form values needed for conditional rendering or effects
	const enablePublicReviews = form.watch('enablePublicReviews')
	const allowNewSubmissions = form.watch('allowNewReviewSubmissions')
	const publicPageUrlSlug = form.watch('publicPageUrlSlug')

	useEffect(() => {
		if (typeof window !== 'undefined') {
			setBaseUrl(window.location.origin)
		}
	}, [])

	useEffect(() => {
		if (currentProgram) {
			form.reset({
				programName: currentProgram.name,
				projectSiteUrl: currentProgram.website_url || '',
				enablePublicReviews: currentProgram.public_reviews_enabled || false,
				publicPageUrlSlug: currentProgram.public_reviews_slug || '',
				logoUrl: currentProgram.public_reviews_logo_url || '',
				mainWebsiteUrl: currentProgram.public_reviews_website_url || '',
				publicPageDescription: currentProgram.public_reviews_description || '',
				showAggregateStats:
					currentProgram.public_reviews_show_statistics ?? true,
				allowNewReviewSubmissions:
					currentProgram.public_reviews_allow_new_submissions ?? true,
				reviewSubmissionWidgetId:
					currentProgram.public_reviews_widget_id || 'none',
				defaultReviewSortOrder:
					currentProgram.public_reviews_default_sort_order || 'newest',
				pageTitleSuffix: currentProgram.public_reviews_page_title_suffix || '',
				metaDescription: currentProgram.public_reviews_meta_description || '',
			})
			setSiteFaviconPreview(currentProgram.favicon_url ?? null)
			setSiteDescriptionPreview(currentProgram.site_description ?? null)
			setFaviconImgFailed(false)
		}
	}, [currentProgram, form])

	useEffect(() => {
		setFaviconImgFailed(false)
	}, [siteFaviconPreview])

	const fetchSiteMetaForUrl = useCallback(
		async (rawUrl: string) => {
			const normalized = normalizeWebsiteUrl(rawUrl)
			if (!normalized) {
				setSiteFaviconPreview(null)
				setSiteDescriptionPreview(null)
				return
			}
			try {
				new URL(normalized)
			} catch {
				return
			}
			setSiteMetaLoading(true)
			try {
				const headers = await getAuthHeaders()
				const res = await fetch(
					`${getApiBaseUrl()}/api/site-meta?url=${encodeURIComponent(normalized)}`,
					{ method: 'GET', cache: 'no-store', headers }
				)
				const data = (await res.json()) as {
					faviconUrl?: string | null
					description?: string | null
				}
				setSiteFaviconPreview(data.faviconUrl ?? null)
				setSiteDescriptionPreview(data.description ?? null)
			} catch {
				setSiteFaviconPreview(null)
				setSiteDescriptionPreview(null)
			} finally {
				setSiteMetaLoading(false)
			}
		},
		[]
	)

	const handleProjectSiteUrlBlur = useCallback(() => {
		const raw = form.getValues('projectSiteUrl')
		void fetchSiteMetaForUrl(raw)
	}, [form, fetchSiteMetaForUrl])

	const handleCopyLink = (link: string) => {
		navigator.clipboard.writeText(link)
		toast({
			title: t('programSettings.copied'),
			description: t('programSettings.linkCopied'),
		})
	}

	const handleSaveSettings = async (values: ProgramSettingsFormValues) => {
		if (!teamId || !currentProgram) return

		setIsSaving(true)
		try {
			const trimmedSite = (values.projectSiteUrl ?? '').trim()
			let resolvedFavicon: string | null = siteFaviconPreview
			let resolvedDescription: string | null = siteDescriptionPreview
			if (trimmedSite) {
				const normalized = normalizeWebsiteUrl(trimmedSite)
				try {
					const headers = await getAuthHeaders()
					const metaRes = await fetch(
						`${getApiBaseUrl()}/api/site-meta?url=${encodeURIComponent(normalized)}`,
						{ method: 'GET', cache: 'no-store', headers }
					)
					const metaJson = (await metaRes.json()) as {
						faviconUrl?: string | null
						description?: string | null
					}
					resolvedFavicon = metaJson.faviconUrl ?? null
					resolvedDescription = metaJson.description ?? null
					setSiteFaviconPreview(resolvedFavicon)
					setSiteDescriptionPreview(resolvedDescription)
				} catch {}
			} else {
				resolvedFavicon = null
				resolvedDescription = null
				setSiteFaviconPreview(null)
				setSiteDescriptionPreview(null)
			}

			const headers = await getAuthHeaders()
			const response = await fetch(
				`${getApiBaseUrl()}/api/programs/${currentProgram.id}`,
				{
					method: 'PUT',
					headers: {
						...headers,
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({
						name: values.programName.trim(),
						website_url:
							trimmedSite === '' ? null : normalizeWebsiteUrl(trimmedSite),
						favicon_url: trimmedSite === '' ? null : resolvedFavicon,
						site_description:
							trimmedSite === '' ? null : resolvedDescription,
						public_reviews_enabled: values.enablePublicReviews,
						public_reviews_slug: values.publicPageUrlSlug,
						public_reviews_logo_url: values.logoUrl,
						public_reviews_website_url: values.mainWebsiteUrl,
						public_reviews_description: values.publicPageDescription,
						public_reviews_show_statistics: values.showAggregateStats,
						public_reviews_allow_new_submissions:
							values.allowNewReviewSubmissions,
						public_reviews_widget_id:
							values.reviewSubmissionWidgetId === 'none'
								? null
								: values.reviewSubmissionWidgetId,
						public_reviews_default_sort_order: values.defaultReviewSortOrder,
						public_reviews_page_title_suffix: values.pageTitleSuffix,
						public_reviews_meta_description: values.metaDescription,
					}),
				}
			)
			if (!response.ok) {
				if (
					response.headers.get('content-type')?.includes('application/json')
				) {
					const data = await response.json()
					console.error('Backend validation error details:', data.details)
					throw new Error(data.error || 'Failed to update program')
				} else {
					throw new Error(
						`Failed to update program. Status: ${response.status}`
					)
				}
			}

			await fetchPrograms(teamId)
			toast({
				title: t('programSettings.success'),
				description: t('programSettings.settingsUpdated'),
			})
		} catch (err: any) {
			console.error('Error updating program:', err)
			const errorMessage = err.message?.includes(
				'Backend validation error details'
			)
				? err.message
				: err.message || t('programSettings.failedToUpdate')
			toast({
				variant: 'destructive',
				title: t('programSettings.error'),
				description: errorMessage,
			})
		} finally {
			setIsSaving(false)
		}
	}

	const handleDeleteProgram = async () => {
		if (!teamId || !currentProgram) return

		setIsDeleting(true)
		try {
			const headers = await getAuthHeaders()
			const response = await fetch(
				`${getApiBaseUrl()}/api/programs/${currentProgram.id}`,
				{
					method: 'DELETE',
					headers,
				}
			)
			if (!response.ok) {
				const data = await response.json().catch(() => ({}))
				throw new Error(
					data.error || `Failed to delete program (status: ${response.status})`
				)
			}
			await fetchPrograms(teamId)
			setIsDeleteDialogOpen(false)
			toast({
				title: t('programSettings.success'),
				description: t('programSettings.settingsUpdated'),
			})
		} catch (err: any) {
			console.error('Error deleting program:', err)
			toast({
				variant: 'destructive',
				title: t('programSettings.error'),
				description: err.message || t('programSettings.failedToDelete'),
			})
		} finally {
			setIsDeleting(false)
		}
	}


	if (!currentProgram && programs.length > 0) {
		return (
			<div className="flex flex-col items-center justify-center h-full py-10 text-center">
				<SettingsIcon className="w-12 h-12 text-muted-foreground mb-4" />
				<h3 className="text-lg font-medium">{t('programSettings.selectProgram')}</h3>
				<p className="text-sm text-muted-foreground">
					{t('programSettings.chooseProgramFromSidebar')}
				</p>
			</div>
		)
	}

	if (!currentProgram && programs.length === 0) {
		return (
			<div className="flex flex-col items-center justify-center h-full py-10 text-center">
				<Layers className="w-12 h-12 text-muted-foreground mb-4" />
				<h3 className="text-lg font-medium">{t('programSettings.noProgramsYet')}</h3>
				<p className="text-sm text-muted-foreground">
					{t('programSettings.createFirstProject')}
				</p>
			</div>
		)
	}

	if (!currentProgram) {
		return (
			<div className="flex justify-center items-center p-8">
				<Loader2 className="h-8 w-8 animate-spin text-primary" />
			</div>
		)
	}

	const sortOptions = [
		{ value: 'newest', label: t('programSettings.sortNewestFirst') },
		{ value: 'oldest', label: t('programSettings.sortOldestFirst') },
		{ value: 'highest', label: t('programSettings.sortHighestRating') },
		{ value: 'lowest', label: t('programSettings.sortLowestRating') },
	]

	return (
		<Form {...form}>
			<form
				onSubmit={form.handleSubmit(handleSaveSettings)}
				className="space-y-8">
				<motion.div
					initial={{ opacity: 0, y: -20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.3 }}
					className="bg-gradient-to-r from-slate-50  sticky top-0 z-10 to-white dark:from-slate-900 dark:to-slate-800 rounded-xl p-6 shadow-sm border border-border/50 mb-6 ">
					<div className="flex items-center justify-between">
						<div className="flex items-center gap-3">
							<div className="p-2 bg-primary/10 rounded-lg">
								<Wrench className="h-5 w-5 text-primary" />
							</div>
							<div>
								<h1 className="text-xl md:text-2xl font-semibold text-foreground">
									<span className="font-bold text-primary">
										{currentProgram.name}
									</span>{' '}
									{t('programSettings.title')}
								</h1>
								<p className="text-sm text-muted-foreground mt-0.5">
									{t('programSettings.manageProjectSettings')}
								</p>
							</div>
						</div>

						<Button
							type="submit"
							disabled={isSaving}
							onClick={() => {
								handleSaveSettings(form.getValues())
							}}>
							{isSaving ? (
								<>
									<Loader2 className="mr-2 h-4 w-4 animate-spin" />
									{t('programSettings.saving')}
								</>
							) : (
								<>
									<Save className="mr-2 h-4 w-4" />
									{t('programSettings.saveSettings')}
								</>
							)}
						</Button>
					</div>
				</motion.div>

				{/* General Settings */}
				<Card>
					<CardHeader>
						<CardTitle className="flex items-center">
							<Settings2 className="mr-2 h-5 w-5 text-muted-foreground" />
							{t('programSettings.generalSettings')}
						</CardTitle>
						<CardDescription>
							{t('programSettings.basicSettingsForProject')}{' '}
							<span className="font-medium text-foreground">
								"{currentProgram.name}"
							</span>
							.
						</CardDescription>
					</CardHeader>
					<CardContent className="space-y-4">
						<FormField
							control={form.control}
							name="programName"
							render={({ field }) => (
								<FormItem>
									<Label htmlFor="program-name">{t('programSettings.programNamePlaceholder')}</Label>
									<Input id="program-name" {...field} />
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="projectSiteUrl"
							render={({ field }) => (
								<FormItem>
									<Label htmlFor="project-site-url">{t('programSettings.siteUrlLabel')}</Label>
									<FormDescription>
										{t('programSettings.siteUrlDescription')}
									</FormDescription>
									<div className="mt-2 flex min-h-10 items-center gap-2.5 rounded-md border border-input bg-background px-3 py-2 shadow-sm focus-within:ring-2 focus-within:ring-ring">
										{siteFaviconPreview && !faviconImgFailed ? (
											<img
												src={siteFaviconPreview}
												alt=""
												className="h-5 w-5 shrink-0 object-contain"
												onError={() => setFaviconImgFailed(true)}
												referrerPolicy="no-referrer"
											/>
										) : (
											<div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary/10 text-sm font-medium text-primary">
												{currentProgram.name?.[0]?.toUpperCase() || '?'}
											</div>
										)}
										<FormControl>
											<Input
												id="project-site-url"
												type="url"
												className="h-auto min-w-0 flex-1 border-0 bg-transparent p-0 shadow-none focus-visible:ring-0"
												placeholder={t('onboarding.websiteUrlPlaceholder')}
												{...field}
												onChange={(e) => {
													field.onChange(e)
													setFaviconImgFailed(false)
												}}
												onBlur={(e) => {
													field.onBlur()
													handleProjectSiteUrlBlur()
												}}
											/>
										</FormControl>
									</div>
									{siteMetaLoading && (
										<p className="text-xs text-muted-foreground pt-1">
											{t('programSettings.loadingSiteMeta')}
										</p>
									)}
									{!siteMetaLoading && siteDescriptionPreview ? (
										<div className="rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-foreground mt-2">
											<p className="text-muted-foreground mb-1">
												{t('programSettings.siteMetaDescriptionPreview')}
											</p>
											<span>{siteDescriptionPreview}</span>
										</div>
									) : null}
									<FormMessage />
								</FormItem>
							)}
						/>
					</CardContent>
				</Card>

				{/* Public Reviews Settings */}
				<Card>
					<CardHeader>
						<CardTitle className="flex items-center">
							<Eye className="mr-2 h-5 w-5 text-muted-foreground" />
							{t('programSettings.publicReviewsPage')}
						</CardTitle>
						<CardDescription>
							{t('programSettings.customizeReviewsDisplay')}
						</CardDescription>
					</CardHeader>
					<CardContent className="space-y-6">
						<FormField
							control={form.control}
							name="enablePublicReviews"
							render={({ field }) => (
								<FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
									<div className="space-y-0.5">
										<Label className="text-base">
											{t('programSettings.enablePublicReviews')}
										</Label>
										<FormDescription>
											{t('programSettings.makeReviewsPublic')}
										</FormDescription>
									</div>
									<FormControl>
										<Switch
											checked={field.value}
											onCheckedChange={field.onChange}
										/>
									</FormControl>
								</FormItem>
							)}
						/>
						{form.watch('enablePublicReviews') && (
							<div className="space-y-4 pt-4 border-t">
								<FormField
									control={form.control}
									name="publicPageUrlSlug"
									render={({ field }) => (
										<FormItem>
											<Label>{t('programSettings.urlSlug')}</Label>
											<Input
												placeholder={t('programSettings.urlSlugPlaceholder')}
												{...field}
											/>
											<FormDescription>
												{t('programSettings.customizeUrlEnd')}
												{' '}Example: {baseUrl}/reviews/YOUR-SLUG-HERE
											</FormDescription>
											{baseUrl && field.value && (
												<div className="mt-2">
													<Label htmlFor="shareable-link" className="sr-only">
														{t('programSettings.shareableLink')}
													</Label>
													<div className="flex items-center space-x-2">
														<Input
															id="shareable-link"
															readOnly
															value={`${baseUrl}/reviews/${field.value}`}
															className="bg-muted"
														/>
														<Button
															type="button"
															variant="outline"
															size="icon"
															onClick={() =>
																handleCopyLink(
																	`${baseUrl}/reviews/${field.value}`
																)
															}>
															<CopyIcon className="h-4 w-4" />
														</Button>
														<Button
															type="button"
															variant="outline"
															size="icon"
															onClick={() =>
																window.open(
																	`${baseUrl}/reviews/${field.value}`,
																	'_blank'
																)
															}>
															<ExternalLink className="h-4 w-4" />
														</Button>
													</div>
												</div>
											)}
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={form.control}
									name="logoUrl"
									render={({ field }) => (
										<FormItem>
											<Label>{t('programSettings.logoUrl')}</Label>
											<Input
												type="url"
												placeholder={t('programSettings.logoUrlPlaceholder')}
												{...field}
											/>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={form.control}
									name="mainWebsiteUrl"
									render={({ field }) => (
										<FormItem>
											<Label>{t('programSettings.mainWebsiteUrl')}</Label>
											<Input
												type="url"
												placeholder={t('programSettings.mainWebsiteUrlPlaceholder')}
												{...field}
											/>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={form.control}
									name="publicPageDescription"
									render={({ field }) => (
										<FormItem>
											<Label>{t('programSettings.pageDescription')}</Label>
											<Textarea
												placeholder={t('programSettings.pageDescriptionPlaceholder')}
												{...field}
											/>
											<FormMessage />
										</FormItem>
									)}
								/>
								{/* <FormField
									control={form.control}
									name="showAggregateStats"
									render={({ field }) => (
										<FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
											<div className="space-y-0.5">
												<Label className="text-base">
													Show Aggregate Statistics
												</Label>
												<FormDescription>
													Display average rating and distribution chart.
												</FormDescription>
											</div>
											<FormControl>
												<Switch
													checked={field.value}
													onCheckedChange={field.onChange}
												/>
											</FormControl>
										</FormItem>
									)}
								/> */}
								<FormField
									control={form.control}
									name="allowNewReviewSubmissions"
									render={({ field }) => (
								<FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
									<div className="space-y-0.5">
										<Label className="text-base">
											{t('programSettings.allowNewReviewSubmissions')}
										</Label>
										<FormDescription>
											{t('programSettings.showWriteReviewButton')}
										</FormDescription>
									</div>
											<FormControl>
												<Switch
													checked={field.value}
													onCheckedChange={field.onChange}
												/>
											</FormControl>
										</FormItem>
									)}
								/>
								<FormField
									control={form.control}
									name="defaultReviewSortOrder"
									render={({ field }) => (
										<FormItem>
											<Label>{t('programSettings.defaultReviewSortOrder')}</Label>
											<Select
												onValueChange={field.onChange}
												value={field.value}
												defaultValue={field.value}>
												<FormControl>
													<SelectTrigger>
														<SelectValue placeholder={t('programSettings.selectSortOrder')} />
													</SelectTrigger>
												</FormControl>
												<SelectContent>
													{sortOptions.map((option) => (
														<SelectItem key={option.value} value={option.value}>
															{option.label}
														</SelectItem>
													))}
												</SelectContent>
											</Select>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
						)}
					</CardContent>
				</Card>

				{/* SEO Settings */}
				<Card>
					<CardHeader>
						<CardTitle className="flex items-center">
							<GlobeIcon className="mr-2 h-5 w-5 text-muted-foreground" />
							{t('programSettings.seoSettings')}
						</CardTitle>
						<CardDescription>
							{t('programSettings.optimizeForSearchEngines')}
						</CardDescription>
					</CardHeader>
					<CardContent className="space-y-4">
						<FormField
							control={form.control}
							name="pageTitleSuffix"
									render={({ field }) => (
										<FormItem>
											<Label>{t('programSettings.pageTitleSuffix')}</Label>
											<Input placeholder={t('programSettings.pageTitleSuffixPlaceholder')} {...field} />
											<FormDescription>
												{t('programSettings.pageTitleSuffixDescription')}
											</FormDescription>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="metaDescription"
									render={({ field }) => (
										<FormItem>
											<Label>{t('programSettings.metaDescription')}</Label>
											<Textarea
												placeholder={t('programSettings.metaDescriptionPlaceholder')}
												{...field}
											/>
									<FormMessage />
								</FormItem>
							)}
						/>
					</CardContent>
				</Card>


				{/* Danger Zone - Only show for team owners/admins and not for invited users */}
				{canManageProjectSettings(currentProgramAccessLevel) && (
					<Card>
					<CardHeader className="bg-red-50 dark:bg-red-900/30 rounded-t-lg">
						<div className="flex items-center">
							<AlertOctagon className="mr-2 h-5 w-5 text-destructive" />
							<CardTitle className="text-destructive">{t('programSettings.dangerZone')}</CardTitle>
						</div>
						<CardDescription className="text-destructive/90 dark:text-red-400/90">
							{t('programSettings.irreversibleActions')}
						</CardDescription>
					</CardHeader>
					<CardContent className="pt-6">
						<div>
							<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
								<div>
									<h4 className="font-semibold text-lg text-foreground">
										{t('programSettings.deleteThisProject')}
									</h4>
									<p className="text-sm text-muted-foreground">
										{t('programSettings.deleteProjectWarning', { projectName: currentProgram.name })}
									</p>
								</div>
								<AlertDialog
									open={isDeleteDialogOpen}
									onOpenChange={setIsDeleteDialogOpen}>
									<AlertDialogTrigger asChild>
										<Button variant="destructive" type="button">
											<Trash2 className="mr-2 h-4 w-4" /> {t('programSettings.deleteProject')}
										</Button>
									</AlertDialogTrigger>
									<AlertDialogContent>
										<AlertDialogHeader>
											<AlertDialogTitle>
												{t('programSettings.areYouAbsolutelySure')}
											</AlertDialogTitle>
											<AlertDialogDescription>
												{t('programSettings.deleteProjectConfirmation', { projectName: currentProgram.name })}
											</AlertDialogDescription>
										</AlertDialogHeader>
										<AlertDialogFooter>
											<AlertDialogCancel disabled={isDeleting}>
												{t('common.cancel')}
											</AlertDialogCancel>
											<AlertDialogAction
												onClick={handleDeleteProgram}
												disabled={isDeleting}
												className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
												{isDeleting && (
													<Loader2 className="mr-2 h-4 w-4 animate-spin" />
												)}
												{t('programSettings.deleteProject')}
											</AlertDialogAction>
										</AlertDialogFooter>
									</AlertDialogContent>
								</AlertDialog>
								</div>
							</div>
						</CardContent>
					</Card>
				)}

			</form>
		</Form>
	)
}

export default ProgramSettings
